import {
  DiscoveryApi,
  FetchApi,
  IdentityApi,
} from '@backstage/frontend-plugin-api';
import { ResponseError } from '@backstage/errors';
import type {
  CloudAgentsActionFailure,
  CloudAgentsActionResult,
  CloudAgentsApi,
  CreateRunRequest,
  EmptyReportedUser,
  ListRunsRequest,
  ReportedUser,
  Run,
  RunSummary,
  Transcript,
} from './types';

const SERVICE_CODE = /^[a-z][a-z0-9_]{0,63}$/;

// Stored exports total at most 32 MiB; allow a 128 KiB/s transfer plus preparation time.
const ARCHIVE_DOWNLOAD_TIMEOUT_MS = 5 * 60_000;

const CONFLICT_MESSAGE: Record<string, string> = {
  archival_not_exhausted:
    'Archival attempts are not exhausted any more, so the archive waiver no longer applies.',
  cleanup_completed: 'Cleanup is already recorded as complete for this run.',
  archive_not_available: 'No session archive is stored for this run.',
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/** Only a well-formed code survives; upstream text is never shown. */
async function readServiceCode(res: Response): Promise<string | undefined> {
  try {
    const body: unknown = await res.json();
    if (isObject(body) && typeof body.code === 'string') {
      return SERVICE_CODE.test(body.code) ? body.code : undefined;
    }
  } catch {
    // An unreadable error body only means there is no service code.
  }
  return undefined;
}

async function toActionFailure(
  res: Response,
  describeFailure: (status: number) => string,
): Promise<CloudAgentsActionFailure> {
  const code = await readServiceCode(res);
  if (res.status === 404) {
    return { ok: false, kind: 'not_found', code, message: 'Run not found.' };
  }
  if (res.status === 409) {
    return {
      ok: false,
      kind: 'conflict',
      code,
      message:
        (code && CONFLICT_MESSAGE[code]) ??
        'The run changed, so this action no longer applies.',
    };
  }
  return {
    ok: false,
    kind: 'request_failed',
    code,
    message: describeFailure(res.status),
  };
}

const UNCERTAIN_FAILURE: CloudAgentsActionFailure = {
  ok: false,
  kind: 'request_failed',
  message: 'The request did not complete. It may or may not have been applied.',
};

export class CloudAgentsClient implements CloudAgentsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly identityApi: IdentityApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
    identityApi: IdentityApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.identityApi = options.identityApi;
  }

  /**
   * The signed-in Backstage user, keyed like the tokens Backstage issues: the
   * auth discovery URL is their issuer and the user entity ref their subject.
   * Without a usable pair there is nothing to report, so the empty user object
   * is sent and the service records its null user; a missing profile only
   * leaves name and email out.
   */
  private async reportedUser(): Promise<ReportedUser | EmptyReportedUser> {
    let issuer: string;
    let subject: string;
    try {
      issuer = await this.discoveryApi.getBaseUrl('auth');
      subject = (await this.identityApi.getBackstageIdentity()).userEntityRef;
    } catch {
      // oxlint-disable-next-line no-console -- Diagnose unattributed dispatches without interrupting them.
      console.warn(
        'Could not read the initiating identity, so the Cloud Agents run is not attributed',
      );
      return {};
    }
    if (!issuer.trim() || !subject.trim()) return {};

    let profile: Awaited<ReturnType<IdentityApi['getProfileInfo']>> | undefined;
    try {
      profile = await this.identityApi.getProfileInfo();
    } catch {
      // oxlint-disable-next-line no-console -- Diagnose missing profile data without interrupting attribution.
      console.warn(
        'Could not read the initiating profile; the Cloud Agents run will retain its identity without name or email',
      );
    }
    return {
      issuer,
      subject,
      ...(profile?.displayName && { name: profile.displayName }),
      ...(profile?.email && { email: profile.email }),
    };
  }

  private async get<T>(path: string, init?: RequestInit): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('proxy');
    const res = await this.fetchApi.fetch(
      `${baseUrl}/cloud-agents${path}`,
      init,
    );
    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }
    return res.json() as Promise<T>;
  }

  async createRun(
    request: Omit<CreateRunRequest, 'user'>,
  ): Promise<{ id: string }> {
    const baseUrl = await this.discoveryApi.getBaseUrl('proxy');
    const user = await this.reportedUser();
    const res = await this.fetchApi.fetch(`${baseUrl}/cloud-agents-dispatch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...request, user }),
    });
    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }
    return res.json() as Promise<{ id: string }>;
  }

  listRuns(request: ListRunsRequest): Promise<{ items: RunSummary[] }> {
    const params = new URLSearchParams();
    request.status?.forEach(status => params.append('status', status));
    if (request.workflowType) {
      params.set('workflowType', request.workflowType);
    }
    params.set('limit', String(request.limit));
    params.set('offset', String(request.offset));
    return this.get(`/v1/runs?${params}`);
  }

  getRun(runId: string): Promise<Run> {
    return this.get(`/v1/runs/${encodeURIComponent(runId)}`);
  }

  async deleteSandbox(runId: string): Promise<void> {
    const baseUrl = await this.discoveryApi.getBaseUrl('proxy');
    const res = await this.fetchApi.fetch(
      `${baseUrl}/cloud-agents-delete/v1/runs/${encodeURIComponent(runId)}/sandbox`,
      { method: 'DELETE' },
    );
    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }
  }

  /**
   * A bodyless POST to the cleanup proxy, which only forwards the two waiver
   * paths. Expected failures are values; success needs the documented body.
   */
  private async postAction(
    path: string,
    isCompletion: (body: unknown) => boolean,
  ): Promise<CloudAgentsActionResult<void>> {
    let res: Response;
    try {
      const baseUrl = await this.discoveryApi.getBaseUrl('proxy');
      res = await this.fetchApi.fetch(
        `${baseUrl}/cloud-agents-cleanup${path}`,
        {
          method: 'POST',
        },
      );
    } catch {
      return UNCERTAIN_FAILURE;
    }
    if (!res.ok) {
      return toActionFailure(
        res,
        status =>
          `The request failed (HTTP ${status}). It may or may not have been applied.`,
      );
    }
    try {
      if (isCompletion(await res.json())) return { ok: true, value: undefined };
    } catch {
      // Falls through: the action was accepted but its reply is unusable.
    }
    return {
      ok: false,
      kind: 'request_failed',
      message:
        'The service replied unexpectedly. The action may have been applied.',
    };
  }

  waiveArchive(runId: string): Promise<CloudAgentsActionResult<void>> {
    return this.postAction(
      `/v1/runs/${encodeURIComponent(runId)}/cleanup/archive/waiver`,
      body =>
        isObject(body) &&
        typeof body.archivedAt === 'string' &&
        typeof body.archiveAvailable === 'boolean',
    );
  }

  acknowledgeManualCleanup(
    runId: string,
  ): Promise<CloudAgentsActionResult<void>> {
    return this.postAction(
      `/v1/runs/${encodeURIComponent(runId)}/cleanup/deletion/waiver`,
      body => isObject(body) && typeof body.deletedAt === 'string',
    );
  }

  async downloadSessionArchive(
    runId: string,
  ): Promise<CloudAgentsActionResult<Blob>> {
    const invalid = (message: string): CloudAgentsActionFailure => ({
      ok: false,
      kind: 'invalid_download',
      message,
    });
    const controller = new AbortController();
    const timeout = setTimeout(
      () => controller.abort(),
      ARCHIVE_DOWNLOAD_TIMEOUT_MS,
    );
    try {
      const baseUrl = await this.discoveryApi.getBaseUrl('proxy');
      const res = await this.fetchApi.fetch(
        `${baseUrl}/cloud-agents/v1/runs/${encodeURIComponent(runId)}/session-archive`,
        { cache: 'no-store', signal: controller.signal },
      );
      if (!res.ok) {
        return await toActionFailure(
          res,
          status =>
            `The archive could not be downloaded (HTTP ${status}). Nothing was saved.`,
        );
      }
      const mediaType = res.headers.get('Content-Type')?.split(';')[0].trim();
      if (mediaType?.toLowerCase() !== 'application/zip') {
        return invalid(
          'The response was not a ZIP archive. Nothing was saved.',
        );
      }
      // A proxy can end a response early, and a Blob built from it would still look like success.
      const declared = res.headers.get('Content-Length');
      const expectedBytes =
        declared !== null && /^[0-9]+$/.test(declared) ? Number(declared) : 0;
      if (!Number.isSafeInteger(expectedBytes) || expectedBytes < 1) {
        return invalid(
          'The response did not declare a usable length, so it cannot be checked. Nothing was saved.',
        );
      }
      const blob = await res.blob();
      if (blob.size !== expectedBytes) {
        return invalid(
          'The download was incomplete or altered in transit. Nothing was saved.',
        );
      }
      return { ok: true, value: blob };
    } catch {
      return invalid(
        controller.signal.aborted
          ? 'The archive download timed out. Nothing was saved.'
          : 'The download was interrupted. Nothing was saved.',
      );
    } finally {
      clearTimeout(timeout);
    }
  }

  getTranscript(runId: string): Promise<Transcript> {
    // A 410 is cacheable by default and browsers reuse it for the same URL; a
    // cached one would hide an archive published afterwards. The service now sends
    // no-store, but responses cached from an older service version still exist, so
    // the client bypasses the cache as well.
    return this.get(`/v1/runs/${encodeURIComponent(runId)}/transcript`, {
      cache: 'no-store',
    });
  }
}
